import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Users, UserCheck, Clock, Calendar, TrendingUp, Briefcase,
  UserPlus, FileCheck, Star, Bell, ChartPie, Building2,
  X, CheckCircle, Plus,
} from "lucide-react";

// -----------------------------------------------------------
// CONFIG
// -----------------------------------------------------------
const API_BASE_URL = "https://hrms.gtplindia.com:8443";

const api = axios.create({
  baseURL: API_BASE_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token") || localStorage.getItem("access_token");
  if (token && token !== "null") {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// -----------------------------------------------------------
// MAIN COMPONENT
// -----------------------------------------------------------
export default function HrDashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(""); // State for the success popup

  const [stats, setStats] = useState({
    totalEmployees: 0,
    presentToday: 0,
    onLeave: 0,
    wfh: 8,
  });

  const [attendanceDetail, setAttendanceDetail] = useState({
    onTime: 0,
    late: 0,
    absent: 0
  });

  const [pipeline, setPipeline] = useState({
    screening: 12,
    interviewing: 6,
    selected: 3,
    offers: 2,
  });

  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [showRewardModal, setShowRewardModal] = useState(false);

  useEffect(() => {
    fetchAllDashboardData();
  }, []);

  // Helper to show success popup
  const triggerSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(""), 3000);
  };

  const fetchAllDashboardData = async () => {
    try {
      setLoading(true);
      const [totalRes, presentRes, leaveRes, attendanceRes] = await Promise.all([
        api.get("/dashboard/total-employees"),
        api.get("/dashboard/present-today"),
        api.get("/dashboard/on-leave"),
        api.get("/dashboard/attendance-summary")
      ]);

      setStats(prev => ({
        ...prev,
        totalEmployees: totalRes.data.total_employees || 0,
        presentToday: presentRes.data.present_today || 0,
        onLeave: leaveRes.data.on_leave || 0,
      }));

      if (attendanceRes.data) {
        setAttendanceDetail({
          onTime: attendanceRes.data.present || 0,
          late: attendanceRes.data.late || 0,
          absent: attendanceRes.data.absent || 0
        });
      }
      setError(null);
    } catch (err) {
      console.error("Dashboard fetch error:", err);
      setError("Server Connection Error.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-teal-600 border-t-transparent"></div>
        <span className="ml-3 text-lg text-gray-700 font-medium font-poppins">Loading Dashboard...</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6 font-roboto relative">
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&family=Roboto:wght@400;500;700&display=swap');
          body { font-family: "Roboto", sans-serif; }
          .font-poppins { font-family: "Poppins", sans-serif; }
        `}
      </style>

      {/* SUCCESS POPUP (TOAST) */}
      {successMsg && (
        <div className="fixed bottom-10 right-10 z-[100] animate-bounce bg-teal-600 text-white px-6 py-3 rounded-xl shadow-2xl flex items-center gap-3 border border-teal-400">
          <CheckCircle size={20} />
          <span className="font-poppins font-semibold text-sm">{successMsg}</span>
        </div>
      )}

      {/* HEADER */}
      <header className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2 font-poppins">
              <ChartPie className="text-teal-600" /> HR Dashboard
            </h1>
            <p className="text-gray-500 mt-1">Real-time HR Analytics</p>
          </div>
          {error && (
            <div className="bg-red-50 border border-red-100 text-red-700 px-4 py-2 rounded-lg text-xs font-medium">
              {error}
            </div>
          )}
        </div>
      </header>

      {/* KPI CARDS */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        <KpiCard icon={<Users className="text-teal-600" />} title="Total Employees" value={stats.totalEmployees} />
        <KpiCard icon={<UserCheck className="text-green-600" />} title="Present Today" value={stats.presentToday} />
        <KpiCard icon={<Calendar className="text-indigo-600" />} title="On Leave" value={stats.onLeave} />
        <KpiCard icon={<Clock className="text-amber-600" />} title="WFH Status" value={stats.wfh} />
      </section>

      {/* MAIN GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 bg-white shadow-sm border border-gray-100 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-gray-700 flex items-center gap-2 mb-6 font-poppins">
            <Clock className="text-teal-600" /> Attendance Summary
          </h2>
          <div className="grid grid-cols-3 gap-8">
            <ProgressCircle label="Present" value={attendanceDetail.onTime} color="text-teal-600" />
            <ProgressCircle label="Late" value={attendanceDetail.late} color="text-orange-500" />
            <ProgressCircle label="Absent" value={attendanceDetail.absent} color="text-red-500" />
          </div>
        </div>

        <QuickActions
          onAddEmployee={() => setShowEmployeeModal(true)}
          onApproveLeaves={() => setShowLeaveModal(true)}
          onAnnouncement={() => setShowAnnouncementModal(true)}
          onReward={() => setShowRewardModal(true)}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-10">
        <HiringPipeline
          pipeline={pipeline}
          onIncrement={(stage) => setPipeline((prev) => ({ ...prev, [stage]: prev[stage] + 1 }))}
        />
        <DepartmentStrength apiInstance={api} />
      </div>

      {/* MODALS */}
      <EmployeeModal open={showEmployeeModal} onClose={() => setShowEmployeeModal(false)} apiInstance={api} onSuccess={triggerSuccess} />
      <LeaveModal open={showLeaveModal} onClose={() => setShowLeaveModal(false)} apiInstance={api} onSuccess={triggerSuccess} />
      <AnnouncementModal open={showAnnouncementModal} onClose={() => setShowAnnouncementModal(false)} apiInstance={api} onSuccess={triggerSuccess} />
      <RewardModal open={showRewardModal} onClose={() => setShowRewardModal(false)} apiInstance={api} onSuccess={triggerSuccess} />
    </div>
  );
}

// ---------------- REUSABLE COMPONENTS ----------------

function KpiCard({ icon, title, value }) {
  return (
    <div className="bg-white border border-gray-100 shadow-sm rounded-xl p-6 flex items-center gap-4 hover:shadow-md transition-all">
      <div className="p-3 bg-gray-50 rounded-lg">{icon}</div>
      <div>
        <p className="text-gray-500 text-xs font-medium uppercase tracking-wider">{title}</p>
        <p className="text-2xl font-bold text-gray-800">{value}</p>
      </div>
    </div>
  );
}

function ProgressCircle({ label, value, color }) {
  const displayVal = value > 100 ? 100 : value;
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (displayVal / 100) * circumference;

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-24 h-24">
        <svg className="w-full h-full transform -rotate-90">
          <circle cx="48" cy="48" r={radius} stroke="#f3f4f6" strokeWidth="8" fill="transparent" />
          <circle
            cx="48" cy="48" r={radius} stroke="currentColor" strokeWidth="8" fill="transparent"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className={`${color} transition-all duration-700`}
            strokeLinecap="round"
          />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center font-bold text-gray-700 text-sm">
          {value}
        </span>
      </div>
      <p className="text-[10px] font-bold text-gray-400 mt-3 uppercase tracking-tighter">{label}</p>
    </div>
  );
}

function QuickActions({ onAddEmployee, onApproveLeaves, onAnnouncement, onReward }) {
  return (
    <div className="bg-white border border-gray-100 shadow-sm rounded-xl p-6">
      <h2 className="text-lg font-semibold text-gray-700 flex items-center gap-2 mb-4 font-poppins">
        <Briefcase className="text-teal-600" /> Management
      </h2>
      <div className="space-y-3">
        <ActionBtn icon={<UserPlus size={18} />} label="Add Employee" onClick={onAddEmployee} />
        <ActionBtn icon={<FileCheck size={18} />} label="Approve Leaves" onClick={onApproveLeaves} />
        <ActionBtn icon={<Bell size={18} />} label="Announcement" onClick={onAnnouncement} />
        <ActionBtn icon={<Star size={18} />} label="Reward Staff" onClick={onReward} />
      </div>
    </div>
  );
}

function ActionBtn({ icon, label, onClick }) {
  return (
    <button className="flex items-center gap-3 p-3 w-full bg-white border border-gray-100 rounded-lg hover:bg-teal-50 transition-all group" onClick={onClick}>
      <span className="p-2 bg-gray-50 group-hover:bg-white rounded-md text-gray-600 group-hover:text-teal-600">{icon}</span>
      <span className="font-medium text-gray-700 text-sm">{label}</span>
    </button>
  );
}

function HiringPipeline({ pipeline, onIncrement }) {
  return (
    <div className="bg-white border border-gray-100 shadow-sm rounded-xl p-6">
      <h2 className="text-lg font-semibold text-gray-700 flex items-center gap-2 mb-6 font-poppins">
        <TrendingUp className="text-teal-600" /> Recruitment
      </h2>
      <ul className="space-y-3">
        {Object.entries(pipeline).map(([key, val]) => (
          <li key={key} className="flex justify-between items-center px-4 py-3 bg-gray-50 rounded-lg">
            <span className="font-medium text-gray-600 capitalize text-sm">{key}</span>
            <div className="flex items-center gap-3">
              <span className="font-bold text-teal-600">{val}</span>
              <button onClick={() => onIncrement(key)} className="p-1 rounded-full bg-white shadow-sm hover:text-teal-600 transition-colors">
                <Plus size={14} />
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function DepartmentStrength({ apiInstance }) {
  const [depts, setDepts] = useState([]);

  useEffect(() => {
    apiInstance.get("/dashboard/department-strength")
      .then(res => { if(Array.isArray(res.data)) setDepts(res.data); })
      .catch(() => {});
  }, [apiInstance]);

  return (
    <div className="bg-white border border-gray-100 shadow-sm rounded-xl p-6">
      <h2 className="text-lg font-semibold text-gray-700 flex items-center gap-2 mb-6 font-poppins">
        <Building2 className="text-teal-600" /> Departments
      </h2>
      <ul className="space-y-3">
        {depts.map((item) => (
          <li key={item.department} className="flex justify-between px-4 py-3 bg-gray-50 rounded-lg">
            <span className="font-medium text-gray-600 text-sm">{item.department}</span>
            <span className="font-bold text-teal-600">{item.count}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// -------- MODALS WITH SUCCESS MESSAGES --------

function EmployeeModal({ open, onClose, apiInstance, onSuccess }) {
  if (!open) return null;
  return (
    <ModalContainer onClose={onClose} title="Add New Employee">
      <form className="space-y-4" onSubmit={async (e) => {
        e.preventDefault();
        const data = new FormData(e.target);
        const payload = {
          full_name: data.get("full_name"),
          email: data.get("email"),
          department: data.get("department")
        };
        try {
            await apiInstance.post("/employees", payload);
            onSuccess("Employee added successfully!");
            onClose();
        } catch (err) { alert("Error adding employee"); }
      }}>
        <Input name="full_name" label="Full Name" required />
        <Input name="email" label="Email" type="email" required />
        <Input name="department" label="Department" required />
        <button className="w-full bg-teal-600 text-white py-3 rounded-lg font-bold hover:bg-teal-700 mt-4 transition-colors font-poppins">Save Employee</button>
      </form>
    </ModalContainer>
  );
}

function LeaveModal({ open, onClose, apiInstance, onSuccess }) {
  if (!open) return null;
  return (
    <ModalContainer onClose={onClose} title="Leave Approvals">
      <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-200">
        <LeaveRequest id={1} name="Amit Kumar" days={2} apiInstance={apiInstance} onSuccess={onSuccess} />
        <LeaveRequest id={2} name="Priya Sharma" days={1} apiInstance={apiInstance} onSuccess={onSuccess} />
        <LeaveRequest id={3} name="Rahul Varma" days={3} apiInstance={apiInstance} onSuccess={onSuccess} />
        <LeaveRequest id={4} name="Sonal Singh" days={5} apiInstance={apiInstance} onSuccess={onSuccess} />
      </div>
    </ModalContainer>
  );
}

function LeaveRequest({ id, name, days, apiInstance, onSuccess }) {
  const handleApprove = async () => {
    try {
        await apiInstance.post("/leave/approve", { leave_id: id });
        onSuccess(`Leave approved for ${name}`);
    } catch (err) { alert("Error approving leave"); }
  };

  return (
    <div className="p-4 bg-gray-50 border border-gray-100 rounded-xl flex justify-between items-center mb-2">
      <div className="flex flex-col">
        <span className="font-bold text-gray-800 text-sm">{name}</span>
        <span className="text-xs text-gray-500">{days} Days Request</span>
      </div>
      <button 
        onClick={handleApprove} 
        className="p-2 bg-white text-green-600 border border-gray-200 rounded-lg hover:bg-green-50 transition-colors"
      >
        <CheckCircle size={20} />
      </button>
    </div>
  );
}

function AnnouncementModal({ open, onClose, apiInstance, onSuccess }) {
  if (!open) return null;
  return (
    <ModalContainer title="New Announcement" onClose={onClose}>
      <form onSubmit={async (e) => {
        e.preventDefault();
        const fd = new FormData(e.target);
        try {
            await apiInstance.post("/announcements", Object.fromEntries(fd));
            onSuccess("Announcement broadcasted!");
            onClose();
        } catch (err) { alert("Error broadcasting"); }
      }} className="space-y-4">
        <Input name="title" label="Title" required />
        <textarea name="message" className="w-full p-4 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-teal-500 min-h-[120px] text-sm font-roboto" placeholder="Broadcast message details..." required />
        <button className="w-full bg-teal-600 text-white py-3 rounded-lg font-bold font-poppins transition-colors">Broadcast</button>
      </form>
    </ModalContainer>
  );
}

function RewardModal({ open, onClose, apiInstance, onSuccess }) {
  if (!open) return null;
  return (
    <ModalContainer title="Reward Recognition" onClose={onClose}>
      <form onSubmit={async (e) => {
        e.preventDefault();
        const fd = new FormData(e.target);
        try {
            await apiInstance.post("/rewards", Object.fromEntries(fd));
            onSuccess("Reward assigned successfully!");
            onClose();
        } catch (err) { alert("Error assigning reward"); }
      }} className="space-y-4">
        <Input name="emp_code" label="Employee Code" required />
        <Input name="reward_title" label="Reward Title" required />
        <button className="w-full bg-teal-600 text-white py-3 rounded-lg font-bold font-poppins transition-colors">Award Now</button>
      </form>
    </ModalContainer>
  );
}

// -------- WRAPPERS --------

function ModalContainer({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50 p-4">
      <div className="bg-white w-full max-w-md p-8 rounded-2xl shadow-2xl relative max-h-[85vh] flex flex-col">
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-bold text-xl text-gray-800 font-poppins">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-800 transition-colors">
            <X size={24} />
          </button>
        </div>
        <div className="overflow-y-auto">
           {children}
        </div>
      </div>
    </div>
  );
}

function Input({ label, ...props }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-bold text-gray-400 uppercase tracking-tight ml-1">{label}</label>
      <input className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-teal-500 text-sm font-roboto transition-all" {...props} />
    </div>
  );
}